import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, createSearchParams } from 'react-router-dom'
import { NavContext } from '../../../context/global_context'
import { deleteRequest, getRequest, postRequest } from '../../../services/base'
import {
  CapitalHarnessContext,
  SET_CAPITAL_HARNESS_PLUGIN_DATA,
  SET_GENERATE_FILE_STATUS,
  SET_SELECTED_IDS,
  SET_UPLOAD_FILE_STATUS,
  SHOW_CAPITAL_HARNESS_CLONE_POPUP,
  SHOW_CAPITAL_HARNESS_UPLOAD_POPUP,
} from './context/context'
import { getSessionStorageData } from '../../../utils/SessionStorage'
import { Dropdown, Flex, Form, Modal, Result, Segmented, Space, Typography } from 'antd'
import { DownloadOutlined, ExclamationCircleOutlined, UploadOutlined, WarningOutlined } from '@ant-design/icons'
import InputText from '../../common/InputText'
import Button from '../../common/Button'
import CapitalHarnessTable from './CapitalHarnessTable'
import CapitalHarnessBulkUpload from './bulk_upload'
import CloneDataFromPopup from './clone_from_model'
import { buildPageUrl, downloadErrorLogs, getDeleteTargetPage } from '../../../utils/common'
import { ProgresSpinner } from '../../common/ProgresSpinner'
import { useIntl } from 'react-intl'
import RecordsCount from '../../common/RecordsCount'
import ErrorCard from '../../../components/common/ErrorCard'
import { notifyError } from '../../../utils/notifications'

// How often the status is checked while an upload or file generation is in progress
const STATUS_POLL_MS = 5000

const CapitalHarness = () => {
  const { vehicle } = useParams()
  const navigate = useNavigate()
  const intl = useIntl()
  const [modal, contextHolder] = Modal.useModal()
  const [capitalHarnessState, capitalHarnessDispatch] = useContext(CapitalHarnessContext)
  const [, dispatch] = useContext(NavContext)
  const [pagination, setPagination] = useState({})
  const [searchValue, setSearchValue] = useState('')
  const [loading, setLoading] = useState(false)
  const [isAllowed, setIsAllowed] = useState(true)
  const [expandedRowKeys, setExpandedRowKeys] = useState([])
  const [expandedDetails, setExpandedDetails] = useState({})
  // Status polling runs only while the backend is busy (an upload being read, or
  // harnesses generating files) and stops once it is idle - see fetchCapHarnessStatusInterval
  const pollTimerRef = useRef(null)
  const pollRequestRef = useRef(0)
  const inProcessIdsRef = useRef([])
  const wasBusyRef = useRef(false)
  const expandedRowKeysRef = useRef([])
  const unmountedRef = useRef(false)

  const vehicle_redirect_url = getSessionStorageData('vehicle')?.search
    ? `/vehicles/?${createSearchParams(getSessionStorageData('vehicle'))}`
    : `/vehicles`

  const currentUrl = useRef(`/api/plugin/capital_harness/?vehicle=${vehicle}&search=${searchValue}`)

  useEffect(() => {
    dispatch({
      type: 'header',
      data: {
        header: intl.formatMessage({ id: 'layout.adminTools' }),
        sub_header: `${intl.formatMessage({ id: 'layout.vehicleList' })} / ${vehicle} / ${intl.formatMessage({ id: 'sidebar.plugin' })} / ${intl.formatMessage({ id: 'sidebar.capitalHarness' })}`,
        id: '',
        show_back_btn: true,
        dtc_details: {},
        back_btn_url: vehicle_redirect_url,
        full_width_container: false,
      },
    })

    // Reset on every mount: StrictMode mounts, cleans up and mounts again
    unmountedRef.current = false
    getCapitalHarnessList(null, true)
    fetchCapHarnessStatusInterval()

    // Changes made elsewhere (another user, another tab) are not polled for while
    // idle; coming back to this tab picks them up, and restarts polling if busy
    let lastReturnRefresh = 0
    const refreshOnReturn = () => {
      // Coming back fires both focus and visibilitychange: refresh once
      if (document.visibilityState !== 'visible' || Date.now() - lastReturnRefresh < 1000) return
      lastReturnRefresh = Date.now()
      getCapitalHarnessList()
      fetchCapHarnessStatusInterval()
    }
    window.addEventListener('focus', refreshOnReturn)
    document.addEventListener('visibilitychange', refreshOnReturn)

    return () => {
      unmountedRef.current = true
      clearTimeout(pollTimerRef.current)
      window.removeEventListener('focus', refreshOnReturn)
      document.removeEventListener('visibilitychange', refreshOnReturn)
    }
  }, [])

  useEffect(() => {
    expandedRowKeysRef.current = expandedRowKeys
  }, [expandedRowKeys])

  const refreshExpandedDetails = async (id) => {
    const res = await getRequest(`/api/plugin/capital_harness_result/?vehicle=${vehicle}&capital_harness=${id}`)
    setExpandedDetails((prev) => ({ ...prev, [id]: res }))
  }

  /**
   * Checks the upload and file-generation status, and keeps checking every
   * STATUS_POLL_MS only while something is in progress. Called on load and after
   * every action that starts work (upload, re-run), which is what restarts it.
   * When a harness finishes, the list (and its expanded files) reload once.
   */
  const fetchCapHarnessStatusInterval = async () => {
    clearTimeout(pollTimerRef.current)
    // A newer call (e.g. right after an upload) takes over the loop
    const request = ++pollRequestRef.current
    const res = await getRequest(`/api/plugin/capital_harness/upload_status_check/${vehicle}/?vehicle=${vehicle}`)
    if (unmountedRef.current || request !== pollRequestRef.current) return
    if (!res?.status) {
      // A failed check (network, 5xx) says nothing about the work: keep checking
      // while it was running, rather than leaving the spinners up for good
      if (wasBusyRef.current) {
        pollTimerRef.current = setTimeout(fetchCapHarnessStatusInterval, STATUS_POLL_MS)
      }
      return
    }
    if (!res?.is_allowed) {
      setIsAllowed(false)
      return
    }

    const inProcessIds = res?.in_process_generate_file_ids || []
    capitalHarnessDispatch({ type: SET_UPLOAD_FILE_STATUS, data: res.data })
    capitalHarnessDispatch({ type: SET_GENERATE_FILE_STATUS, data: inProcessIds })

    const finishedIds = inProcessIdsRef.current.filter((id) => !inProcessIds.includes(id))
    const busy = res.data === true || inProcessIds.length > 0
    if (finishedIds.length > 0 || (wasBusyRef.current && !busy)) {
      getCapitalHarnessList()
      finishedIds
        .filter((id) => expandedRowKeysRef.current.includes(id))
        .forEach((id) => refreshExpandedDetails(id))
    }
    inProcessIdsRef.current = inProcessIds
    wasBusyRef.current = busy

    if (busy) {
      pollTimerRef.current = setTimeout(fetchCapHarnessStatusInterval, STATUS_POLL_MS)
    }
  }

  const getCapitalHarnessList = async (url = null, showLoader = false) => {
    if (showLoader) setLoading(true)
    let response = await getRequest(url || currentUrl.current)
    capitalHarnessDispatch({ type: SET_CAPITAL_HARNESS_PLUGIN_DATA, data: response.results })
    if (response?.status === false) {
      notifyError(dispatch, intl.formatMessage({ id: 'capitalHarness.loadError' }))
    }
    setPagination(response.pagination)
    if (showLoader) setLoading(false)
  }

  const downloadCapitalHarnessFiles = async (download_option = null) => {
    const downloadOptionLabel =
      download_option === 'all'
        ? intl.formatMessage({ id: 'generic.all' })
        : intl.formatMessage({ id: 'forms.selected' })

    modal.confirm({
      icon: <ExclamationCircleOutlined />,
      title: intl.formatMessage({ id: 'capitalHarness.downloadConfirmationTitle' }),
      content: intl.formatMessage(
        { id: 'capitalHarness.downloadConfirmationContent' },
        { downloadOption: downloadOptionLabel }
      ),
      centered: true,
      maskClosable: true,
      okText: intl.formatMessage({ id: 'forms.download' }),
      cancelText: intl.formatMessage({ id: 'forms.cancel' }),
      onOk: async () => {
        dispatch({
          type: 'loader',
          data: { caption: intl.formatMessage({ id: 'capitalHarness.downloading' }), showLoader: true },
          notification: {},
        })

        let formData = new FormData()
        if (download_option === 'all') {
          formData.append('download_files', 'all')
        } else {
          capitalHarnessState.selectedIds.forEach((element) => {
            formData.append('download_files', element)
          })
        }
        formData.append('vehicle', vehicle)

        let res = await postRequest(`/api/plugin/capital_harness/bulk/download/`, formData, true)
        // Was `if (res?.msg)`: a failure carrying no message fell through and
        // saved the error object as the file. Same guard as the other exports.
        if (res?.status === false || !(res instanceof Blob)) {
          dispatch({
            type: 'notification',
            data: { notification_type: 'error', msg: intl.formatMessage({ id: 'capitalHarness.downloadError' }), loader: true },
          })
        } else {
          const url = window.URL.createObjectURL(new Blob([res]))
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', `${vehicle}_def.zip`)
          document.body.appendChild(link)
          link.click()
          link.remove()
          dispatch({
            type: 'notification',
            data: { notification_type: 'success', msg: intl.formatMessage({ id: 'capitalHarness.fileDownloaded' }), loader: true },
          })
        }
      },
    })
  }

  const deleteCapitalHarness = async (record) => {
    modal.confirm({
      icon: <ExclamationCircleOutlined />,
      title: intl.formatMessage({ id: 'capitalHarness.deleteConfirmationTitle' }),
      content: intl.formatMessage({ id: 'capitalHarness.deleteConfirmationContent' }),
      centered: true,
      maskClosable: true,
      okText: intl.formatMessage({ id: 'forms.delete' }),
      okType: 'danger',
      cancelText: intl.formatMessage({ id: 'forms.cancel' }),
      onOk: async () => {
        const res = await deleteRequest(`/api/plugin/capital_harness/${record.id}/?vehicle=${vehicle}`)
        // The result used to be discarded, so the success toast below fired even
        // when the delete failed. base.js returns {status: false}, never rejects.
        if (res?.status === false) {
          notifyError(dispatch, intl.formatMessage({ id: 'capitalHarness.deleteError' }))
          return
        }
        currentUrl.current = buildPageUrl(currentUrl.current, getDeleteTargetPage(pagination))
        getCapitalHarnessList(null, true)
        dispatch({
          type: 'notification',
          data: { notification_type: 'success', msg: intl.formatMessage({ id: 'capitalHarness.deleted' }) },
        })
      },
    })
  }

  const reRunFileGeneration = async (record) => {
    modal.confirm({
      icon: <ExclamationCircleOutlined />,
      title: intl.formatMessage({ id: 'capitalHarness.runFileGenerationTitle' }),
      content: intl.formatMessage(
        { id: 'capitalHarness.runFileGenerationContent' },
        { name: record?.name || '' }
      ),
      centered: true,
      maskClosable: true,
      okText: intl.formatMessage({ id: 'capitalHarness.runFileGeneration' }),
      cancelText: intl.formatMessage({ id: 'forms.cancel' }),
      onOk: async () => {
        dispatch({
          type: 'loader',
          data: { caption: intl.formatMessage({ id: 'capitalHarness.generating' }), showLoader: true },
          notification: {},
        })
        let res = await postRequest(`/api/plugin/capital_harness/run_file_generation/${vehicle}/${record.id}/`)
        // Clear the loader FIRST: the 'loader' reducer case overwrites
        // `notification`, so both toasts below used to be wiped immediately.
        dispatch({ type: 'loader', data: {}, notification: {} })
        if (res?.status) {
          dispatch({
            type: 'notification',
            data: { notification_type: 'success', msg: intl.formatMessage({ id: 'capitalHarness.generateStarted' }) },
          })
        } else {
          dispatch({
            type: 'notification',
            data: { notification_type: 'error', msg: intl.formatMessage({ id: 'capitalHarness.generateError' }) },
          })
        }
        fetchCapHarnessStatusInterval()
        if (expandedRowKeys.length > 0) {
          const id = expandedRowKeys[0]
          const detailRes = await getRequest(`/api/plugin/capital_harness_result/?vehicle=${vehicle}&capital_harness=${id}`)
          setExpandedDetails((prev) => ({ ...prev, [id]: detailRes }))
        }
      },
    })
  }

  const onClone = (record, typeKey) => {
    const selectedType = {
      name: intl.formatMessage({
        id:
          typeKey === 'copy_circuit_details_from'
            ? 'capitalHarness.copyCircuitDetailsFrom'
            : 'capitalHarness.copyDevicesAndConnectorAttributesFrom',
      }),
      value: typeKey,
    }
    capitalHarnessDispatch({
      type: SHOW_CAPITAL_HARNESS_CLONE_POPUP,
      data: record,
      showClonePopup: true,
      selectedType,
    })
  }

  const onDownloadLogs = (record) => {
    downloadErrorLogs(record?.log?.error, 'error.log')
  }

  const toggleExpand = async (record) => {
    const id = record.id
    if (expandedRowKeys.includes(id)) {
      setExpandedRowKeys([])
      return
    }
    setExpandedRowKeys([id])
    const res = await getRequest(`/api/plugin/capital_harness_result/?vehicle=${vehicle}&capital_harness=${id}`)
    setExpandedDetails((prev) => ({ ...prev, [id]: res }))
  }

  const locationPath = `/vehicles/${vehicle}/plugins/capital_harness`
  const menuItems = useMemo(() => [
    { label: intl.formatMessage({ id: 'sidebar.salesCodeComplexity' }), value: `/vehicles/${vehicle}/plugins/sales_code_complexity` },
    { label: intl.formatMessage({ id: 'sidebar.defPlugin' }), value: `/vehicles/${vehicle}/plugins/def` },
    { label: intl.formatMessage({ id: 'sidebar.capitalHarness' }), value: `/vehicles/${vehicle}/plugins/capital_harness` },
  ], [vehicle, intl])

  const dataSource = useMemo(() => (capitalHarnessState?.data || []).map((item, index) => {
    return ({
        key: item.id || index,
        id: item.id,
        name: item.name,
        abbreviation: item.abbreviation,
        updated_at: item.updated_at,
        status: item.status,
        log: item.log,
        capital_harness_file: item.capital_harness_file,
        file_names: item.file_names,
        description: item.description,
      })
    }), [capitalHarnessState?.data])

  const downloadMenuItems = useMemo(() => [
    {
      key: 'all',
      label: intl.formatMessage({ id: 'capitalHarness.downloadAllFiles' }),
      onClick: () => downloadCapitalHarnessFiles('all'),
    },
    {
      key: 'selected',
      label: intl.formatMessage({ id: 'capitalHarness.downloadSelectedFiles' }),
      disabled: (capitalHarnessState?.selectedIds?.length || 0) === 0,
      onClick: () => downloadCapitalHarnessFiles('selected'),
    },
  ], [capitalHarnessState?.selectedIds, intl])

  if (!isAllowed) {
    return (
      <div className="page capital-harness not-permitted">
        <Flex className="title">
          <Segmented
            options={menuItems}
            value={locationPath}
            onChange={(value) => navigate(value)}
          />
        </Flex>

        <ErrorCard 
          icon={WarningOutlined}
          iconStyle={{ color: 'var(--color-warning-icon, #FAAD14)'}}
          title={intl.formatMessage({ id: 'capitalHarness.notPermitted' })}
          sub_title={intl.formatMessage({ id: 'capitalHarness.notConfiguredForMake' })}
        />
      </div>
    )
  }

  return (
    <div className="page capital-harness">
      <Flex className="title">
        <Segmented
          options={menuItems}
          value={locationPath}
          onChange={(value) => navigate(value)}
        />
      </Flex>

      <Flex vertical className="body">
        <Flex align="center" justify="space-between" gap={16} wrap>
          <Flex align="center" gap={8} style={{ flex: 1 }}>
            <Form style={{ width: '100%' }}>
              <InputText
                name="search_value"
                placeholder={intl.formatMessage({ id: 'capitalHarness.searchPlaceholder' })}
                value={searchValue}
                type="search"
                onChange={(value) => setSearchValue(value)}
                isRequired={false}
                wrapperClass="search-wrapper"
                onSubmit={() => {
                  currentUrl.current = `/api/plugin/capital_harness/?vehicle=${vehicle}&search=${searchValue}`
                  getCapitalHarnessList(null, true)
                }}
              />
            </Form>
          </Flex>

          <Space size="middle">
            {capitalHarnessState?.in_process_upload_files && (
              <ProgresSpinner caption={intl.formatMessage({ id: 'capitalHarness.fileUploadInProgress' })} className="upload-inprogress" size="medium" />
            )}

            <Dropdown menu={{ items: downloadMenuItems }} trigger={['click']}>
              <Button icon={<DownloadOutlined />}>
                {intl.formatMessage({ id: 'forms.download' })}
              </Button>
            </Dropdown>

            <Button
              type="primary"
              icon={<UploadOutlined />}
              onClick={() => capitalHarnessDispatch({ type: SHOW_CAPITAL_HARNESS_UPLOAD_POPUP, data: true })}
            >
              {intl.formatMessage({ id: 'capitalHarness.uploadNewFile' })}
            </Button>
          </Space>
        </Flex>

        <CapitalHarnessTable
          dataSource={dataSource}
          pagination={pagination}
          loading={loading}
          expandedRowKeys={expandedRowKeys}
          expandedDetails={expandedDetails}
          selectedIds={capitalHarnessState?.selectedIds || []}
          inProcessGenerateFileIds={capitalHarnessState?.in_process_generate_file_ids || []}
          onToggleExpand={toggleExpand}
          onRunFileGeneration={reRunFileGeneration}
          onClone={onClone}
          onDelete={deleteCapitalHarness}
          onSelect={(record) => capitalHarnessDispatch({ type: SET_SELECTED_IDS, data: record.id })}
          onDownloadLogs={onDownloadLogs}
          getCapitalHarnessList={getCapitalHarnessList}
        />

        {pagination &&
        pagination?.totalPages <= 1 &&
        pagination?.totalCount > 0 && (
          <Typography.Text>
            <RecordsCount count={pagination?.totalCount} />
          </Typography.Text>
        )}
      </Flex>

      <CapitalHarnessBulkUpload
        onClose={() => {
          getCapitalHarnessList(null, true)
          fetchCapHarnessStatusInterval()
        }}
      />
      <CloneDataFromPopup />
      {contextHolder}
    </div>
  )
}

export default CapitalHarness
